import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/site/Layout";
import Breadcrumb from "@/components/site/Breadcrumb";
import NewsletterForm from "@/components/site/NewsletterForm";
import { newsArticles } from "@/mocks/articles";
import { newsCategories } from "@/mocks/categories";
import { formatViews } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";

function ArticlePage() {
  const { slug = "" } = useParams();
  const article = newsArticles.find((a) => a.slug === slug);
  const [copied, setCopied] = useState(false);

  const related = article
    ? [
        ...newsArticles.filter((a) => a.category === article.category && a.slug !== article.slug),
        ...newsArticles.filter((a) => a.category !== article.category),
      ].slice(0, 4)
    : [];

  usePageMeta({
    title: article ? `${article.title} - 吃瓜新闻快讯` : "文章不存在 - 吃瓜新闻",
    description: article
      ? `${article.summary}「${article.categoryName}」栏目 · 吃瓜新闻每日快讯为您速递。`
      : "您访问的文章不存在，请返回首页浏览其它快讯。",
    canonical: `https://youbijing.com.cn/article/${slug}`,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://youbijing.com.cn/" },
              { "@type": "ListItem", position: 2, name: `${article.categoryName}快讯`, item: `https://youbijing.com.cn/category/${article.category}` },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.summary,
            image: [article.image],
            datePublished: `${article.dateLabel}T${article.timeLabel}:00+08:00`,
            dateModified: `${article.dateLabel}T${article.timeLabel}:00+08:00`,
            author: { "@type": "Organization", name: "吃瓜新闻" },
            publisher: { "@type": "Organization", name: "吃瓜新闻" },
            inLanguage: "zh-CN",
            mainEntityOfPage: `https://youbijing.com.cn/article/${article.slug}`,
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-file-search-line" />
          </span>
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">这篇文章走丢了</h1>
          <p className="mt-2 text-foreground-600">内容可能已被更新或移除，去看看今日要闻吧。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-9">
        <div className="mx-auto max-w-3xl">
          <Breadcrumb
            crumbs={[
              { label: "首页", to: "/" },
              { label: `${article.categoryName}快讯`, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${article.category}`}
                className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50"
              >
                {article.categoryName}
              </Link>
              {article.tags.slice(0, 2).map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2 py-1 text-xs text-secondary-900">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-[15px] leading-7 text-foreground-600 md:text-base">{article.summary}</p>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-3 text-sm text-foreground-500">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-pencil-ruler-line text-sm" />
                  </span>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line" /> {article.dateLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line" /> {article.timeLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line" /> {formatViews(article.views)} 阅读
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-200 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {copied ? (
                  <>
                    <i className="ri-check-line text-secondary-700" /> 已复制链接
                  </>
                ) : (
                  <>
                    <i className="ri-link" /> 复制链接
                  </>
                )}
              </button>
            </div>
          </header>

          <figure className="mt-7 overflow-hidden rounded-xl border border-background-200">
            <img src={article.image} alt={article.title} title={`${article.title}吃瓜新闻配图`} className="w-full" />
          </figure>

          <article className="mt-8">
            <div className="rounded-r-lg border-l-4 border-accent-500 bg-accent-100/70 px-5 py-3 text-sm leading-6 text-accent-900">
              <i className="ri-lightbulb-line mr-1" />
              导读：{article.summary}
            </div>
            <div className="mt-7 space-y-6 text-[15px] leading-8 text-foreground-800">
              {article.content.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </article>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-background-100 px-5 py-4">
            <span className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
              <i className="ri-price-tag-3-line text-primary-500" />
              {article.tags.map((t) => (
                <span key={t} className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-700">
                  #{t}
                </span>
              ))}
            </span>
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看更多{article.categoryName}快讯 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* 相关推荐 */}
        <section className="mx-auto mt-14 max-w-3xl" aria-label="相关推荐">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">相关推荐</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/article/${r.slug}`}
                className="group flex gap-3 overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300 hover:bg-background-100"
              >
                <img
                  src={r.image}
                  alt={r.title}
                  title={`${r.title}相关推荐`}
                  className="h-28 w-28 shrink-0 object-cover"
                />
                <div className="min-w-0 p-3">
                  <span className="text-xs font-medium text-secondary-700">{r.categoryName}</span>
                  <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-foreground-800 group-hover:text-primary-700">
                    {r.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-400">
                    {r.dateLabel} · {formatViews(r.views)} 阅读
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-12 max-w-3xl">
          <NewsletterForm />
        </section>
      </div>
    </Layout>
  );
}

export default ArticlePage;